/**
 * STS SPA - Single Entry Point
 * Vue 3 + Vue Router + Pinia
 */
import { createApp } from 'vue';
import { createPinia } from 'pinia';
import { createI18n } from 'vue-i18n';
import axios from 'axios';

// App root component
import App from './App.vue';
import { mountDevBanner } from './utils/devBanner';

// Router
import router from './router';

// Stores
import { useAuthStore } from './stores/auth';

// i18n messages
import en from '../locales/en.json';
import ko from '../locales/ko.json';
import ja from '../locales/ja.json';
import zhTW from '../locales/zh-tw.json';
import zhCN from '../locales/zh-cn.json';

// Styles
import '../assets/styles/app.css';
import '../assets/styles/editor.scss';
import 'remixicon/fonts/remixicon.css';

// Global components
import LayoutHeader from './components/layout/AppHeader.vue';
import PageTitle from '../components/layout/page_title.vue';
import LangSelector from './components/layout/LangSelector.vue';
import PingBadge from '../components/form/ping_badge.vue';
import vSelect from 'vue-select';

// Config
import { config } from '../commons/config.js';
import sharedMethods from '../commons/app-func.js';

// API 참조 설정 함수
import { setAuthStore, setRouter, setI18n } from './api/index.js';

// Create Vue app
const app = createApp(App);

// Pinia
const pinia = createPinia();
app.use(pinia);

// i18n
const i18n = createI18n({
    locale: 'ko',
    fallbackLocale: 'en',
    legacy: false,
    globalInjection: true,
    messages: { ko, en, ja, zhTW, zhCN },
});
app.use(i18n);

// Router
app.use(router);

// Shared methods
app.use(sharedMethods);

// Global components
app.component('layout-header', LayoutHeader);
app.component('page-title', PageTitle);
app.component('lang-selector', LangSelector);
app.component('v-select', vSelect);
app.component('ping-badge', PingBadge);

// Global properties
app.config.globalProperties.axios = axios;
app.config.globalProperties.config = config;

// Axios interceptors
axios.interceptors.response.use(
    (response) => {
        if (response.data.result === 'LOGIN') {
            router.push('/account/login');
        }
        return response;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Initialize auth store
// IMPORTANT: Get auth store AFTER Pinia is registered
console.log('[Main] 🎯 Getting auth store...');
const authStore = useAuthStore();

// API interceptor에서 사용할 참조 설정
console.log('[Main] Setting API references...');
setAuthStore(authStore);
setRouter(router);
setI18n(i18n);
console.log('[Main] API references set');

// 앱을 먼저 마운트 (UI 빠르게 표시)
console.log('[Main] ✅ Mounting app...');
app.mount('#app');
mountDevBanner(); // 개발서버(dev.wmdpass.com)에서만 상단 DEV 표시
console.log('[Main] 🎉 App mounted!');

// 백그라운드에서 초기화 (토큰 등)
authStore.initialize()
    .then(() => {
        console.log('[Main] Auth initialization completed');
    })
    .catch((error) => {
        console.error('[Main] ⚠️  Auth initialization failed:', error);
    });

